import type { BaseSyntheticEvent, FC, ReactElement, ReactNode } from "react"

import eq from "lodash-es/eq"
import findIndex from "lodash-es/findIndex"
import isEmpty from "lodash-es/isEmpty"
import last from "lodash-es/last"
import map from "lodash-es/map"

import { Divider } from "@/3514/components/shared/divider/divider.component"
import { EDividerColor } from "@/3514/components/shared/divider/divider.types"
import { type TScreenBreakpoints, type TUseListSentinel, useListSentinel, useScreenBreakpoints } from "@/3514/hooks"
import { EAxisDirection, buildCssClassName, captureElementByAttribute } from "@/3514/utils"

import Item from "./coach.navigation-stepper.item.component"
import classes from "./coach.navigation-stepper.styles"
import {
    ECoachNavigationStepperBulletOrder as EBulletOrder,
    ECoachNavigationStepperBulletCompletion as ECompletion,
    ECoachNavigationStepperMode as EMode,
    type TCoachNavigationStepperBullet as TBullet
} from "./coach.navigation-stepper.types"

type TProps = {
    bullets: TBullet[]
    mode: EMode
    onListHover?(index: number): Promise<void>
    wrapperClassName?: string
}

/**
 * Coach's side navigation stepper.
 * Design: {@link https://www.figma.com/design/QLhX4KcIViCdMD12xdfvXq/Coach?node-id=2940-29752&m=dev}
 *
 * @param bullets sessions navigation items
 * @param mode enum for stepper view and internal logic
 * @param withOnboardingSession boolean flag to show onboarding session title
 *
 * @return ReactElement
 */
const Stepper: FC<TProps> = ({
    bullets,
    mode,
    onListHover = undefined,
    wrapperClassName = undefined
}: TProps): ReactElement => {
    const isSessions: boolean = eq(mode, EMode.Sessions)
    const isModules: boolean = eq(mode, EMode.Modules)

    const isAccessible: boolean = isSessions || isModules

    const currentIndex: number = findIndex<TBullet>(bullets, (b: TBullet): boolean => eq(b.order, EBulletOrder.Current))

    const breakpoints: TScreenBreakpoints = useScreenBreakpoints()

    const sentinel: TUseListSentinel = useListSentinel({
        direction: EAxisDirection.Horizontal,
        width: breakpoints.xs || breakpoints.sm ? "30px" : "50px",
        isEnabled: bullets.length > 4 || Boolean(breakpoints.xs || breakpoints.sm)
    })

    function renderDivider(variant: EDividerColor): ReactNode {
        return isSessions || isModules ? (
            <Divider
                className={classes.stepper.divider({ isModulesCut: isModules, isSessionsCut: isSessions })}
                variant={variant}
                margins={{ top: "15px", bottom: "auto" }}
            />
        ) : null
    }

    function handleBulletHover(e: BaseSyntheticEvent): void {
        captureElementByAttribute(e, (index: string): void => {
            if (!eq(Number(index), bullets[currentIndex]?.pageId)) void onListHover?.(Number(index))
        })
    }

    return isEmpty(bullets) ? null : (
        <div
            className={buildCssClassName(
                classes.stepper.container({
                    mode
                }),
                { [wrapperClassName]: wrapperClassName }
            )}
        >
            <div
                className={classes.stepper.list()}
                ref={sentinel.parentRef}
                tabIndex={-1}
                {...(isAccessible && {
                    role: "tab",
                    ...(!!onListHover && {
                        onMouseOver: handleBulletHover,
                        onFocus: handleBulletHover
                    })
                })}
            >
                {renderDivider(EDividerColor.SemiDarkBlue)}

                {map<TBullet, ReactElement>(
                    bullets,
                    (b: TBullet, idx: number, { length }: TBullet[]): ReactElement => (
                        <Item
                            key={`c-ns-${b.pageId}-${idx}`}
                            stepperMode={mode}
                            isLast={eq(idx, length - 1)}
                            isNextCompleted={bullets[idx + 1]?.completion === ECompletion.Completed}
                            {...b}
                        />
                    )
                )}

                {renderDivider(
                    last<TBullet>(bullets).completion === ECompletion.NotCompleted
                        ? EDividerColor.MediumBlue
                        : EDividerColor.SemiDarkBlue
                )}

                {sentinel.element}
            </div>
        </div>
    )
}

Stepper.displayName = "CoachNavigationStepper"

export { Stepper as CoachNavigationStepper }
